"use strict";
// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
Object.defineProperty(exports, "__esModule", { value: true });
exports.DeclarativeAgentGenerator = void 0;
const tslib_1 = require("tslib");
/**
 * @author yuqzho@microsoft.com
 */
const teamsfx_api_1 = require("@microsoft/teamsfx-api");
const lodash_1 = require("lodash");
const path_1 = tslib_1.__importDefault(require("path"));
const featureFlags_1 = require("../../../common/featureFlags");
const stringUtils_1 = require("../../../common/stringUtils");
const question_1 = require("../../../question");
const CopilotGptManifestUtils_1 = require("../../driver/teamsApp/utils/CopilotGptManifestUtils");
const common_1 = require("../../utils/common");
const developerPortalScaffoldUtils_1 = require("../../developerPortalScaffoldUtils");
const defaultGenerator_1 = require("../defaultGenerator");
const generator_1 = require("../generator");
const templateNames_1 = require("../templates/templateNames");
const utils_1 = require("../utils");
const helper_1 = require("./helper");
/**
 * Generator for copilot extensions including declarative copilot with no plugin,
 * declarative copilot with API plugin from scratch, declarative copilot with existing plugin,
 * and API plugin from scratch.
 */
class DeclarativeAgentGenerator extends defaultGenerator_1.DefaultTemplateGenerator {
    componentName = "declarative-agent-from-scratch-generator";
    activate(context, inputs) {
        return [
            templateNames_1.TemplateNames.DeclarativeAgentBasic,
            templateNames_1.TemplateNames.DeclarativeAgentWithActionFromScratch,
            templateNames_1.TemplateNames.DeclarativeAgentWithActionFromScratchBearer,
            templateNames_1.TemplateNames.DeclarativeAgentWithActionFromScratchOAuth,
            templateNames_1.TemplateNames.DeclarativeAgentWithExistingAction,
            templateNames_1.TemplateNames.DeclarativeAgentWithTypeSpec,
            templateNames_1.TemplateNames.DeclarativeAgentWithActionFromMCP,
        ].includes(inputs[question_1.QuestionNames.TemplateName]);
    }
    async getTemplateInfos(context, inputs, destinationPath, actionContext) {
        const auth = inputs[question_1.QuestionNames.ApiAuth];
        const appName = inputs[question_1.QuestionNames.AppName];
        const language = inputs[question_1.QuestionNames.ProgrammingLanguage];
        const safeProjectNameFromVS = language === "csharp" ? inputs[question_1.QuestionNames.SafeProjectName] : undefined;
        const solutionNameFromVS = language === "csharp" ? inputs[question_1.QuestionNames.SolutionName] : undefined;
        const MCPServerType = inputs[question_1.QuestionNames.MCPServerType];
        const isLocalMCP = MCPServerType === "local";
        const MCPForDAServerUrl = inputs[question_1.QuestionNames.MCPForDAServerUrl];
        try {
            const replaceMap = {
                ...generator_1.Generator.getDefaultVariables(inputs[question_1.QuestionNames.TemplateName] === templateNames_1.TemplateNames.DeclarativeAgentWithTypeSpec
                    ? (0, stringUtils_1.convertToAlphanumericOnly)(appName)
                    : appName, safeProjectNameFromVS, solutionNameFromVS, inputs.targetFramework, inputs.placeProjectFileInSolutionDir === "true"),
                DeclarativeCopilot: "true",
                MicrosoftEntra: auth === question_1.ApiAuthOptions.microsoftEntra().id ? "true" : "",
                IsLocalMCP: isLocalMCP ? "true" : "",
                ...(isLocalMCP
                    ? this.processMCPLocalServers(inputs)
                    : MCPForDAServerUrl
                        ? {
                            MCPForDAServerUrl,
                            ServerName: (0, helper_1.deriveMCPServerNameFromUrl)(MCPForDAServerUrl),
                        }
                        : {}),
            };
            const templateName = inputs[question_1.QuestionNames.TemplateName];
            (0, lodash_1.merge)(actionContext?.telemetryProps, {
                ["template-name" /* telemetryProperties.templateName */]: templateName,
                ["is-microsoft-entra" /* telemetryProperties.isMicrosoftEntra */]: auth === question_1.ApiAuthOptions.microsoftEntra().id ? "true" : "",
                ["need-add-plugin-from-existing" /* telemetryProperties.needAddPluginFromExisting */]: inputs[question_1.QuestionNames.ActionType] === question_1.ActionStartOptions.existingPlugin().id.toString(),
            });
            return Promise.resolve((0, teamsfx_api_1.ok)([
                {
                    templateName,
                    language: language,
                    replaceMap,
                },
            ]));
        }
        catch (error) {
            return (0, teamsfx_api_1.err)(error);
        }
    }
    async post(context, inputs, destinationPath, actionContext) {
        const teamsManifestPath = path_1.default.join(destinationPath, teamsfx_api_1.AppPackageFolderName, teamsfx_api_1.ManifestTemplateFileName);
        const declarativeCopilotManifestPathRes = await CopilotGptManifestUtils_1.copilotGptManifestUtils.getManifestPath(teamsManifestPath);
        if (declarativeCopilotManifestPathRes.isErr()) {
            // only return error in da existing action case
            if (templateNames_1.TemplateNames.DeclarativeAgentWithExistingAction === inputs[question_1.QuestionNames.TemplateName]) {
                return (0, teamsfx_api_1.err)(declarativeCopilotManifestPathRes.error);
            }
            return (0, teamsfx_api_1.ok)({});
        }
        if (featureFlags_1.featureFlagManager.getBooleanValue(featureFlags_1.FeatureFlags.SensitivityLabelEnabled)) {
            // best-effort
            await (0, utils_1.setGeneralSensitivityLabel)(context, declarativeCopilotManifestPathRes.value);
        }
        if (featureFlags_1.featureFlagManager.getBooleanValue(featureFlags_1.FeatureFlags.MCPForDA) &&
            templateNames_1.TemplateNames.DeclarativeAgentWithActionFromMCP === inputs[question_1.QuestionNames.TemplateName]) {
            const result = await (0, helper_1.generateForMCPForDA)(destinationPath, inputs);
            return result;
        }
        if (templateNames_1.TemplateNames.DeclarativeAgentWithExistingAction === inputs[question_1.QuestionNames.TemplateName]) {
            const addPluginRes = await (0, helper_1.addExistingPlugin)(declarativeCopilotManifestPathRes.value, inputs[question_1.QuestionNames.PluginManifestFilePath], inputs[question_1.QuestionNames.PluginOpenApiSpecFilePath], "action_1", context, this.componentName);
            if (addPluginRes.isErr()) {
                return (0, teamsfx_api_1.err)(addPluginRes.error);
            }
            else {
                if (inputs.platform === teamsfx_api_1.Platform.CLI || inputs.platform === teamsfx_api_1.Platform.VS) {
                    const warningMessage = (0, common_1.outputScaffoldingWarningMessage)(addPluginRes.value.warnings);
                    if (warningMessage) {
                        context.logProvider.info(warningMessage);
                    }
                }
                return (0, teamsfx_api_1.ok)({ warnings: addPluginRes.value.warnings });
            }
        }
        else {
            if (inputs.teamsAppFromTdp) {
                const res = await developerPortalScaffoldUtils_1.developerPortalScaffoldUtils.updateFilesForTdp(context, inputs.teamsAppFromTdp, inputs);
                if (res.isErr()) {
                    return (0, teamsfx_api_1.err)(res.error);
                }
            }
            return (0, teamsfx_api_1.ok)({});
        }
    }
    /**
     * Process selected MCP local servers from inputs and format for template
     * Handles both single and multiple server selection for backward compatibility
     */
    processMCPLocalServers(inputs) {
        const selectedOptions = inputs[question_1.QuestionNames.MCPLocalServer];
        if (selectedOptions && !Array.isArray(selectedOptions)) {
            throw new teamsfx_api_1.SystemError(this.componentName, "processMCPLocalServers", "Expected MCPLocalServer input to be an array");
        }
        // Handle empty/invalid selection
        if (!selectedOptions || selectedOptions.length === 0) {
            return {
                MCPLocalServers: [],
            };
        }
        // Map selected options to server configs
        const servers = selectedOptions.map((option, index) => {
            // Validate option structure
            if (!option.data || typeof option.data !== "string") {
                throw new teamsfx_api_1.SystemError(this.componentName, "processMCPLocalServers", "Invalid option data structure");
            }
            const serverData = JSON.parse(option.data);
            // Validate parsed data
            if (!serverData.identifier || !Array.isArray(serverData.args)) {
                throw new teamsfx_api_1.SystemError(this.componentName, "processMCPLocalServers", "Invalid server data format");
            }
            if (!serverData.command || typeof serverData.command !== "string") {
                throw new teamsfx_api_1.UserError(this.componentName, "processMCPLocalServers", "Invalid or missing command in server data");
            }
            return {
                name: option.id,
                identifier: serverData.identifier,
                command: serverData.command,
                args: serverData.args.map((arg) => `"${arg}"`).join(", "),
                notLast: index < selectedOptions.length - 1,
            };
        });
        return {
            MCPLocalServers: servers,
        };
    }
}
exports.DeclarativeAgentGenerator = DeclarativeAgentGenerator;
//# sourceMappingURL=generator.js.map